import { Eye, EyeOff, Globe, LogIn, Moon, Sun } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, login } from '../../api/client';
import { usePrefs } from '../../app/prefs';
import type { Locale } from '../../i18n';
import { CityBackground } from './CityBackground';
import { applyUserPrefs, logoFor, useBranding, useSetMe } from './session';

export function LoginPage() {
  const { t } = useTranslation();
  const theme = usePrefs((s) => s.theme);
  const locale = usePrefs((s) => s.locale);
  const setTheme = usePrefs((s) => s.setTheme);
  const setLocale = usePrefs((s) => s.setLocale);
  const branding = useBranding();
  const setMe = useSetMe();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const b = branding.data;
  const logo = logoFor(b, theme, 'full');
  const text = b?.marketing[locale] ?? b?.marketing.en;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await login(username, password);
      applyUserPrefs(me);
      setMe(me);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.code : 'NETWORK');
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <CityBackground theme={theme} />
      <div className="login-veil" aria-hidden />
      <div className="login-tools">
        <button
          type="button"
          className="btn btn-glass"
          onClick={() => setLocale((locale === 'en' ? 'ro' : 'en') as Locale)}
          aria-label={t('settings.language')}
        >
          <Globe size={15} /> {locale === 'en' ? 'Română' : 'English'}
        </button>
        <button
          type="button"
          className="btn btn-glass"
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          aria-label={t('settings.theme')}
        >
          {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
        </button>
      </div>
      <main className="login-layout">
        <section className="login-hero">
          {logo ? (
            <img className="login-logo" src={logo} alt={b?.product_name ?? ''} />
          ) : (
            <div className="login-product">{b?.product_name}</div>
          )}
          {text && (
            <>
              <h1>{text.title}</h1>
              <p>{text.subtitle}</p>
            </>
          )}
        </section>
        <form className="login-card" onSubmit={submit} noValidate>
          <div className="login-card-head">
            <span className="login-card-product">{b?.product_name}</span>
            <h2>{t('login.title')}</h2>
          </div>
          <label className="field">
            <span>{t('login.username')}</span>
            <input
              className="input input-lg"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>{t('login.password')}</span>
            <span className="input-affix">
              <input
                className="input input-lg"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={t(showPassword ? 'login.hidePassword' : 'login.showPassword')}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </span>
          </label>
          {error && (
            <div className="form-error" role="alert">
              {t(`login.errors.${error}` as 'login.errors.INVALID_CREDENTIALS', {
                defaultValue: t('login.errors.GENERIC'),
              })}
            </div>
          )}
          <button type="submit" className="btn btn-primary btn-lg" disabled={busy || !username || !password}>
            <LogIn size={16} /> {busy ? t('login.signingIn') : t('login.submit')}
          </button>
          <p className="login-foot muted">UI {__APP_VERSION__}</p>
        </form>
      </main>
    </div>
  );
}
